import { api } from "@/lib/api";
import type { Pagination } from "@/lib/types";

/** 指定業者。API の DesignatedVendor（lib/enums/designated_vendor.py）と一致させる。 */
export type DesignatedVendor = "kyocera" | "hirata" | "shinto" | "kohoku";

/**
 * 指定業者の表示名。
 *
 * 見積依頼ファイルの取り込みで、どの得意先の依頼かをこの区分から決める。
 * 同じ区分は 1 社にしか設定できない（2 社あると取り込み先が決まらないため）。
 */
export const DESIGNATED_VENDOR_LABEL: Record<DesignatedVendor, string> = {
  kyocera: "京セラ",
  hirata: "平田機工",
  shinto: "新東工業",
  kohoku: "湖北精工",
};

/** 指定業者の選択肢（表示順は DESIGNATED_VENDOR_LABEL の定義順）。 */
export const DESIGNATED_VENDOR_OPTIONS = (
  Object.keys(DESIGNATED_VENDOR_LABEL) as DesignatedVendor[]
).map((key) => ({ key, label: DESIGNATED_VENDOR_LABEL[key] }));

/** 取引先（API の PartnerResponse に対応）。 */
export interface Partner {
  partner_id: number;
  partner_code: string;
  partner_name: string;
  /** 取引停止。true は得意先・納入先の選択候補から外れる（過去の見積・案件は残る）。 */
  disabled: boolean;
  /**
   * 得意先として使う。見積・案件の得意先の選択肢はこのフラグで絞る。
   * 直送先と兼ねる相手がいるので、用途は区分ではなく 2 つのフラグで持つ。
   */
  is_client: boolean;
  /** 直送先として使う。納入先の選択肢はこのフラグで絞る。 */
  is_ship_to: boolean;
  /**
   * 指定業者。見積依頼ファイルの取り込みで、どの得意先の依頼かをこの区分から
   * 決める。区分なし（null）の取引先は取り込みの対象にならない。
   */
  designated_vendor: DesignatedVendor | null;
  partner_kana: string | null;
  postal_code: string | null;
  address: string | null;
  sub_address: string | null;
  phone_number: string | null;
  phone_sub_number: string | null;
  fax_number: string | null;
  /** FAX 番号 2。代表と直通のように 2 本あるときの副番号。 */
  fax_sub_number: string | null;
  /** 担当者名。部署・敬称・複数名を含んだまま、客先の一覧表の表記で持つ。 */
  contact_name: string | null;
  email_address: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PartnerListResponse {
  items: Partner[];
  pagination: Pagination;
}

export interface PartnerListParams {
  keyword?: string;
  /** 未指定なら取引中・取引停止の両方。得意先・納入先の選択肢では false を渡す。 */
  disabled?: boolean;
  /** 得意先の選択肢では true を渡す（渡さないと直送先も候補に並ぶ）。 */
  isClient?: boolean;
  /** 納入先の選択肢では true を渡す。 */
  isShipTo?: boolean;
  sortKey?: string;
  ascending?: boolean;
  limit: number;
  offset: number;
}

export function listPartners(params: PartnerListParams): Promise<PartnerListResponse> {
  const query = new URLSearchParams();
  query.set("limit", String(params.limit));
  query.set("offset", String(params.offset));
  if (params.keyword) query.set("keyword", params.keyword);
  if (params.disabled != null) query.set("disabled", String(params.disabled));
  if (params.isClient != null) query.set("is_client", String(params.isClient));
  if (params.isShipTo != null) query.set("is_ship_to", String(params.isShipTo));
  if (params.sortKey) query.set("sort_key", params.sortKey);
  if (params.ascending != null) query.set("ascending", String(params.ascending));
  return api.get<PartnerListResponse>(`/partners?${query}`);
}

/** 登録・更新で送れる項目（partner_id や日時は送らない）。 */
export type PartnerInput = Partial<
  Pick<
    Partner,
    | "partner_code"
    | "partner_name"
    | "disabled"
    | "is_client"
    | "is_ship_to"
    | "designated_vendor"
    | "partner_kana"
    | "postal_code"
    | "address"
    | "sub_address"
    | "phone_number"
    | "phone_sub_number"
    | "fax_number"
    | "fax_sub_number"
    | "contact_name"
    | "email_address"
  >
>;

export function createPartner(input: PartnerInput): Promise<Partner> {
  return api.post<Partner>("/partners", input);
}

/** 部分更新。渡した項目だけ反映される。 */
export function updatePartner(
  partnerId: number,
  input: PartnerInput,
): Promise<Partner> {
  return api.patch<Partner>(`/partners/${partnerId}`, input);
}

export function deletePartner(partnerId: number): Promise<void> {
  return api.delete<void>(`/partners/${partnerId}`);
}
